import { useMemo, useState, type MouseEvent } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { CalendarClock, Info } from 'lucide-react'
import { DataTable, DateValue, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useComplianceMutations, useOccurrences } from '../api'
import {
  COMPLIANCE_CATEGORY_LABEL, OCCURRENCE_STATUS_LABEL, OCCURRENCE_STATUS_TONE,
} from '../types'
import type { OccurrenceStatus, OccurrenceView } from '../types'

/**
 * What is owed, when, and whether it was done.
 *
 * <p>The calendar starts empty and stays that way until somebody says what this company owes.
 * Nothing statutory is shipped: which returns are due depends on the jurisdiction, the licence
 * and the sector, and a plausible-looking seeded list is one that eventually gets reported
 * against.
 */
export function ComplianceCalendarPage() {
  useBreadcrumb()
  const thisYear = new Date().getFullYear()

  const [entityId, setEntityId] = useState('')
  const [status, setStatus] = useState('')
  const [category, setCategory] = useState('')
  const [year, setYear] = useState(thisYear)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(50)

  const occurrences = useOccurrences({ entityId, status, category, year, page, size })
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const actions = useComplianceMutations()

  const [closing, setClosing] = useState<OccurrenceView | null>(null)
  const [outcome, setOutcome] = useState<{ status: OccurrenceStatus; reference: string; note: string }>(
    { status: 'DONE', reference: '', note: '' })
  const [error, setError] = useState<string | null>(null)
  const [generated, setGenerated] = useState<string | null>(null)

  const columns = useMemo<ColumnDef<OccurrenceView, unknown>[]>(() => [
    { id: 'itemName', header: 'Obligation', accessorFn: (row) => row.itemName ?? '—' },
    { id: 'authority', header: 'Owed to', accessorFn: (row) => row.authority ?? '—' },
    { id: 'periodLabel', header: 'Period', accessorFn: (row) => row.periodLabel },
    {
      id: 'dueDate',
      header: 'Due',
      accessorFn: (row) => row.dueDate,
      cell: ({ row }) => (row.original.dueDate
        ? (
          <span className={row.original.daysUntilDue != null && row.original.daysUntilDue < 0
            && row.original.status !== 'DONE' ? 'text-[var(--color-danger)]' : undefined}>
            <DateValue value={row.original.dueDate} />
          </span>
        )
        : <span className="text-[var(--color-muted)]" title={row.original.dueNote ?? ''}>Not set</span>),
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.status,
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          tone={OCCURRENCE_STATUS_TONE[row.original.status as OccurrenceStatus] ?? 'neutral'}
          label={OCCURRENCE_STATUS_LABEL[row.original.status as OccurrenceStatus]
            ?? row.original.status.toLowerCase()}
        />
      ),
    },
    { id: 'ownerName', header: 'Owner', accessorFn: (row) => row.ownerName ?? 'Nobody' },
    {
      id: 'evidenceCount',
      header: 'Evidence',
      accessorFn: (row) => row.evidenceCount,
      cell: ({ row }) => (row.original.evidenceCount > 0
        ? `${row.original.evidenceCount} file(s)`
        : (
          <span className="text-[var(--color-muted)]">
            {row.original.requiresEvidence ? 'Required, none yet' : 'Not required'}
          </span>
        )),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={(event: MouseEvent) => {
            event.stopPropagation()
            setError(null)
            setClosing(row.original)
            setOutcome({
              status: row.original.status === 'OPEN'
                ? 'DONE' : (row.original.status as OccurrenceStatus),
              reference: row.original.reference ?? '',
              note: row.original.outcomeNote ?? '',
            })
          }}
        >
          Record outcome
        </Button>
      ),
    },
  ], [])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Compliance calendar"
        subtitle="What is owed, to whom, and whether it was done."
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setGenerated(null)
              actions.generate.mutate(year, {
                onSuccess: (result) => setGenerated(result.note),
                onError: (caught) =>
                  setGenerated(toApiProblem(caught)?.detail ?? 'That year could not be generated'),
              })
            }}
            disabled={actions.generate.isPending}
          >
            <CalendarClock className="size-4" aria-hidden="true" />
            Fill in {year}
          </Button>
        }
      />

      <Card className="flex items-start gap-2 p-3 text-sm text-[var(--color-body)]">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Nothing statutory ships with this product. Which returns you owe, to whom and by when
        depends on your jurisdiction, your licence and your sector — so the calendar starts
        empty, and every obligation on it is one somebody here put there.
      </Card>

      {generated && (
        <p role="status" className="rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm">
          {generated}
        </p>
      )}

      <DataTable<OccurrenceView>
        columns={columns}
        data={occurrences.data}
        isLoading={occurrences.isPending}
        error={occurrences.error}
        onRetry={() => void occurrences.refetch()}
        page={page}
        size={size}
        onPageChange={setPage}
        onSizeChange={setSize}
        emptyTitle="Nothing on the calendar"
        emptyDescription="Add an obligation on the Obligations screen, then fill in a year."
        toolbar={
          <>
            <Input
              aria-label="Year"
              type="number"
              className="w-24"
              value={String(year)}
              onChange={(event) => { setYear(Number(event.target.value)); setPage(0) }}
            />
            <Select
              aria-label="Legal entity"
              value={entityId}
              onChange={(event) => { setEntityId(event.target.value); setPage(0) }}
            >
              <option value="">Every entity</option>
              {entities.data?.map((entity) => (
                <option key={entity.id} value={entity.id}>{entity.name}</option>
              ))}
            </Select>
            <Select
              aria-label="Status"
              value={status}
              onChange={(event) => { setStatus(event.target.value); setPage(0) }}
            >
              <option value="">Any status</option>
              {Object.entries(OCCURRENCE_STATUS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
            <Select
              aria-label="Category"
              value={category}
              onChange={(event) => { setCategory(event.target.value); setPage(0) }}
            >
              <option value="">Any category</option>
              {Object.entries(COMPLIANCE_CATEGORY_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </>
        }
      />

      <FormDialog
        open={closing != null}
        onOpenChange={(open) => { if (!open) { setClosing(null); setError(null) } }}
        title={closing ? `${closing.itemName} · ${closing.periodLabel}` : ''}
        description="What happened to this period."
        submitLabel="Save"
        loading={actions.completeOccurrence.isPending}
        error={error}
        onSubmit={() => {
          if (!closing) return
          void (async () => {
            setError(null)
            try {
              await actions.completeOccurrence.mutateAsync({
                id: closing.id,
                status: outcome.status,
                reference: outcome.reference || null,
                outcomeNote: outcome.note || null,
              })
              setClosing(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That outcome could not be recorded')
            }
          })()
        }}
      >
        {closing && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="outcome-status">Outcome</Label>
              <Select
                id="outcome-status"
                className="w-full"
                value={outcome.status}
                onChange={(event) =>
                  setOutcome({ ...outcome, status: event.target.value as OccurrenceStatus })}
              >
                {Object.entries(OCCURRENCE_STATUS_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="outcome-reference">Reference</Label>
              <Input
                id="outcome-reference"
                value={outcome.reference}
                onChange={(event) => setOutcome({ ...outcome, reference: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                The receipt number, acknowledgement or file reference the authority gave back.
              </p>
            </div>
            <div>
              <Label htmlFor="outcome-note">Reason</Label>
              <Textarea
                id="outcome-note"
                rows={3}
                value={outcome.note}
                onChange={(event) => setOutcome({ ...outcome, note: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Required for a waived or missed period. An unexplained waiver is the first thing
                an auditor asks about.
              </p>
            </div>
            {closing.requiresEvidence && closing.evidenceCount === 0 && (
              <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                This obligation is configured to need evidence, and nothing is attached yet.
                Marking it done will be refused until something is.
              </p>
            )}
          </div>
        )}
      </FormDialog>
    </div>
  )
}
